import { useCurrentStep } from 'api/guide';
import LoadingButton from 'components/shared/LoadingButton';
import { useXAAGuideProgress } from 'contexts/GuideProgressContext';
import { useIdPConfigContext } from 'contexts/IdPConfigContext';
import { useSaveSSOConfigFields } from 'contexts/SSOConfigFieldsContext';
import useToast from 'hooks/useToast';
import { isEmpty } from 'lodash';
import React, { useCallback } from 'react';
import { ToastSeverity } from 'types/toast';
import { useStepNavigate } from 'utils/navigation';

interface IXAAContinueButtonProps {
	disabled?: boolean;
	onSaved?: () => void;
}

// Cross-App Access is a machine-to-machine token exchange, not an interactive login, so
// the XAA guide has no test step. The last step saves directly (like SCIM's finish); on
// success it hands control to the saved-confirmation panel (onSaved) instead of silently
// redirecting home, mirroring how SSO stays on the page and shows a Done button.
const XAAContinueButton: React.FC<IXAAContinueButtonProps> = ({
	disabled = false,
	onSaved = undefined
}) => {
	const { xaaGuideStepNum } = useIdPConfigContext();

	const { data: currentStepData } = useCurrentStep();

	const { navigateToNextStep } = useStepNavigate();
	const { setStepCompleted } = useXAAGuideProgress();
	// `save` is withheld by useSaveSSOConfigFields whenever the GUIDE-level validation has
	// any error, while this page's disabled state and error alert come from the CURRENT-STEP
	// validation. An error on another step therefore leaves the button enabled with nothing
	// rendered, so the save has to report it itself rather than return quietly.
	const { save, fieldsUniqueErrorMessages } = useSaveSSOConfigFields();
	const { addToast } = useToast();

	const onClick = useCallback(async () => {
		if (currentStepData?.isLastStep) {
			if (!save) {
				// Guide-level validation is blocking the save. Surface the messages when they
				// belong to a field the guide renders (the admin can go fix them); otherwise
				// fall back to the generic save error so Save is never a silent no-op.
				addToast({
					severity: ToastSeverity.Error,
					content: isEmpty(fieldsUniqueErrorMessages)
						? intl.tr('xaaConfig.save.saveErrorMessage')
						: fieldsUniqueErrorMessages.join('\n')
				});
				return;
			}
			// Only mark the final step complete after the save actually succeeds - a failed
			// save must leave the step incomplete so the user can retry.
			const saved = await save();
			if (!saved) return;
			setStepCompleted(xaaGuideStepNum!);
			onSaved?.();
		} else {
			setStepCompleted(xaaGuideStepNum!);
			navigateToNextStep();
		}
	}, [
		addToast,
		currentStepData?.isLastStep,
		fieldsUniqueErrorMessages,
		navigateToNextStep,
		onSaved,
		save,
		setStepCompleted,
		xaaGuideStepNum
	]);

	return (
		<LoadingButton
			data-testid="guide-continue-button"
			variant="contained"
			disabled={disabled}
			onClick={onClick}
		>
			{intl.tr(
				currentStepData?.isLastStep ? 'general.save' : 'general.continue'
			)}
		</LoadingButton>
	);
};

export default XAAContinueButton;
